<template>
	<div class="table-container">
		<slot />
		<el-table
			ref="jrTable"
			:key="key"
			v-loading="loading"
			:data="tableData"
			:size="size"
			:height="inTableHeight"
			:border="border"
			:stripe="stripe"
			:header-cell-class-name="headerCellClass"
			:row-class-name="rowClassName"
			:row-key="rowKeys"
			:cell-style="cellStyle"
			:default-expand-all="defaultExpandAll"
			v-bind="$attrs"
			v-on="$listeners"
			@select="handleSelect"
			@select-all="handleSelectAll"
			@selection-change="handleSelectChange"
			@row-click="handleRowClick"
			@sort-change="changeTableSort"
		>
			<el-table-column
				v-if="selection"
				type="selection"
				:reserve-selection="reserveSelection"
				width="50"
				align="center"
			></el-table-column>
			<el-table-column v-if="index" type="index" width="70" align="center" label="序号"></el-table-column>
			<el-table-column v-if="expand" type="expand" :width="expandWidth">
				<template slot-scope="scope">
					<slot :scope="scope" name="expand"></slot>
				</template>
			</el-table-column>
			<el-table-column
				v-for="(item, columnIndex) in column.filter(item => !item.isHidden)"
				:key="columnIndex"
				:label="item.label"
				:prop="item.prop"
				:sortable="item.sort"
				:min-width="item.minWidth"
				:width="item.width"
				:align="item.align || 'center'"
				:header-align="item.align || 'center'"
				:fixed="item.fixed"
				:show-overflow-tooltip="item.truncate"
				label-class-name="text-sm"
				class-name="text-sm"
			>
				<template slot-scope="scope">
					<slot v-if="item.slot" :scope="scope" :name="item.slot"></slot>
					<template v-else>{{ scope.row[item.prop] }}</template>
				</template>
			</el-table-column>
		</el-table>

		<jr-pagination
			v-if="showPagination"
			:limit="pageOptions.limit"
			:page.sync="pageOptions.page"
			:total="pageOptions.total"
			:hidden="pageHide"
			:pag-sizes="pageOptions.pageSizes"
			@change="paginationChange"
		></jr-pagination>
	</div>
</template>

<script>
	import { Loading } from 'element-ui' // 导入Loading指令
	import JrPagination from '@/components/Pagination'

	export default {
		name: 'JrTable',
		components: {
			JrPagination
		},
		directives: {
			loading: Loading.directive // 注册Loading指令
		},
		props: {
			// 表格数据
			tableData: {
				required: true,
				type: Array,
				default: () => []
			},
			// 表头配置
			column: {
				required: false,
				type: Array,
				default: () => []
			},
			// 是否展示序号
			index: {
				required: false,
				type: Boolean,
				default: () => false
			},
			// 是否支持选择
			selection: {
				required: false,
				type: Boolean,
				default: () => false
			},
			// 是否在数据更新之后保留之前选中的数据（需指定 row-key）（即跨页选中数据）
			reserveSelection: {
				required: false,
				type: Boolean,
				default: () => false
			},
			// 是否支持下拉内容
			expand: {
				required: false,
				type: Boolean,
				default: () => false
			},
			// 是否默认展开所有行
			defaultExpandAll: {
				required: false,
				type: Boolean,
				default: () => false
			},
			// 展开行单元格宽度
			expandWidth: {
				required: false,
				type: Number,
				default: 50
			},
			// 展开行是否开启点击行展开行
			expandRowClick: {
				required: false,
				type: Boolean,
				default: () => false
			},
			// 是否仅支持单选
			single: {
				type: Boolean,
				required: false,
				default: false
			},
			// 是否显示加载
			loading: {
				type: Boolean,
				required: false
			},
			// 是否带有纵向边框
			border: {
				type: Boolean,
				required: false,
				default: true
			},
			// 是否为斑马纹 table
			stripe: {
				type: Boolean,
				required: false,
				default: true
			},
			// table尺寸，medium / small / mini
			size: {
				required: false,
				type: String,
				default: 'small'
			},
			// 表格高度是否固定，或采用自适应高度
			fixedHeight: {
				required: false,
				type: Boolean,
				default: () => false
			},
			// 表格的高度，string/number
			height: {
				type: [Number, String, Function],
				default: () => null
			},
			// 表头行的 className 的回调方法，也可以使用字符串为所有表头行设置一个固定的 className。
			headerCellClass: {
				required: false,
				type: String,
				default: 'default-th'
			},
			// 行内的className
			rowClassName: {
				type: Function,
				required: false,
				default: null
			},
			cellStyle: {
				type: Function,
				required: false,
				default: null
			},
			/* 行内的rowKey */
			rowKeys: {
				type: Function,
				required: false,
				default: null
			},
			pageHide: {
				type: Boolean,
				required: false,
				default: false
			},
			/**
			 * 分页参数
			 * limit:每页显示的条数
			 * page:当前页码
			 * total:总条数
			 * pageSizes: 显示多少条集合
			 * hidden: 是否显示分页
			 */
			pageOptions: {
				type: Object,
				required: false,
				default() {
					return {
						limit: 10,
						page: 1,
						total: 0,
						pageSizes: [10, 20, 30, 50],
						hidden: false
					}
				}
			},
			/* 是否显示分页栏 */
			showPagination: {
				type: Boolean,
				required: false,
				default: true
			}
		},
		data() {
			return {
				key: Symbol(),
				multipleSelection: [],
				inTableHeight: null,
				pagination: {
					limit: 10,
					page: 1,
					total: 0,
					pageSizes: [10, 20, 30, 50],
					hidden: false
				}
			}
		},
		watch: {
			/**
			 * 数据变化后 高度自适应
			 */
			tableData() {
				this.$nextTick(() => {
					this.changeTableHight()
				})
			},
			/**
			 *  监听列表头变化 给table添加key
			 */
			column() {
				this.$nextTick(() => {
					this.key = Symbol()
				})
			}
		},
		created() {
			// 扩展配置，目的是引用组件不用把配置全写一遍
			this.pagination = Object.assign({}, this.pagination, this.pageOptions)

			//该阶段可以接收父组件的传递参数
			this.inTableHeight = this.height
		},
		mounted() {
			this.$nextTick(() => {
				//表格高度自适应浏览器大小
				this.changeTableHight()
				if (!this.height) {
					window.onresize = () => {
						this.changeTableHight()
					}
				}
			})
		},
		destroyed() {
			//高度自适应事件注销
			window.onresize = null
		},
		methods: {
			// 当用户手动勾选数据行的 Checkbox 时触发的事件
			handleSelect(selection, row) {
				this.$emit('selectEvent', { selection, row })
			},
			// 当选择项发生变化时会触发该事件
			handleSelectChange(rows) {
				if (this.single) {
					if (rows.length <= 0) {
						return
					} else if (rows.length === 1) {
						// console.log('当前选中的数据：');
						// console.log(rows);
					} else {
						const obj = rows[rows.length - 1] //取最后被选中的行
						rows = []
						rows[0] = obj
						this.multipleSelection = rows
						//先清空
						this.$refs.jrTable.clearSelection()
						//再赋值
						this.$refs.jrTable.toggleRowSelection(rows[0])
					}
					this.$emit('selectionChange', rows)
				} else {
					this.$emit('selectionChange', rows)
				}
			},
			// 当用户手动勾选全选 Checkbox 时触发的事件
			handleSelectAll(selection) {
				this.$emit('selectAll', selection)
			},
			// 翻页
			paginationChange(pager) {
				const { page, limit } = pager
				this.$emit('page-change', { page, limit })
			},
			// 点击一行
			handleRowClick(row, column, event) {
				if (this.expand && this.expandRowClick) {
					const $table = this.$refs.jrTable
					this.tableData.map(item => {
						if (row.id !== item.id) {
							$table.toggleRowExpansion(item, false)
						}
					})
					$table.toggleRowExpansion(row)
				}
				this.$emit('handleRowClick', row)
			},

			/**
			 * 高度自适应
			 * 当表格展示空间小于460按460px展示，大于的时候高度填充
			 */
			changeTableHight() {
				if (this.height) {
					//如果有传进来高度就取消自适应
					this.inTableHeight = this.height
					this.$refs.jrTable.doLayout()
					return
				}
				let tableHeight = window.innerHeight || document.body.clientHeight
				//高度设置
				const disTop = this.$refs.jrTable.$el
				//如果表格上方有元素则减去这些高度适应窗口，16是底下留白部分
				// console.log(disTop.offsetTop);
				// 160 wei顶部高度
				// console.log('offsetTop：',disTop.offsetTop)
				tableHeight -= disTop.offsetTop + 16 + 150 + 90
				this.inTableHeight = this.fixedHeight ? (tableHeight < 460 ? 460 : tableHeight) : null
				//重绘表格
				this.$refs.jrTable.doLayout()
			},
			// 表格排序事件
			changeTableSort(column) {
				this.$emit('changeTableSort', column)
			}
		}
	}
</script>

<style lang="scss" scoped>
	.table-container {
		padding: 10px;
		border-radius: 5px;
		background-color: #ffffff;

		::v-deep {
			.el-table {
				border: solid 1px #0c72fe;
				border-radius: 6px;
				overflow: hidden;

				// 修复右上角边框间隙问题
				&::before {
					// display: none;
				}

				.default-th {
					background-color: #4686d2;
					color: #ffffff;
					font-size: 18px;
					font-weight: 500;

					&.el-table__cell {
						padding: 10px 5px;

						.cell {
							padding: 0 10px;
						}
					}
				}

				.el-table__body-wrapper td {
					font-size: 16px;
					color: #333;
				}

				td.el-table__cell,
				th.el-table__cell.is-leaf {
					border-color: #0c72fe;
				}

				// 去除单元格的右边框
				&.el-table--border .el-table__cell,
				.el-table__body-wrapper .el-table--border.is-scrolling-left ~ .el-table__fixed {
					border-right: none;
				}

				.el-table__row {
					&:hover {
						background-color: #f5faff;
					}
				}

				.th3 {
					font-weight: bold;
					font-size: 14px;
					border-bottom: 1px solid #dfe6ec !important;
					color: #909399;
					background-color: #f8f8f9;
				}

				.caret-wrapper {
					height: 20px;
				}

				.sort-caret.ascending {
					top: -2px;
				}

				.sort-caret.descending {
					bottom: 1px;
				}
			}

			.el-table--small {
				font-size: 15px;
				.th3 {
					font-size: 15px;
				}
			}
		}
	}

	.el-table .th {
		color: #048;
	}

	.el-table .th2 {
		font-weight: bold;
		border-bottom: 1px solid #eef !important;
		color: #048;
	}

	.el-table .th3 {
		font-weight: bold;
		border-bottom: 1px solid #dfe6ec !important;
		color: #515a6e;
		background-color: #f8f8f9;
	}

	.el-table ::v-deep.el-table__expand-icon {
		//visibility: hidden;
	}

	.el-table ::v-deep.el-table__expanded-cell {
		padding: 0;
	}

	.el-table ::v-deep {
		// 给右侧固定列添加下边距
		.el-table__fixed-right {
			height: auto !important;
			bottom: 10px !important; //具体值是多少根据你横向滚动条的高度进行设置
		}

		// 去掉固定列下方的横线
		.el-table__fixed::before,
		.el-table__fixed-right::before {
			display: none;
		}

		// 改变el-table横向滚动条样式
		.el-table__body-wrapper::-webkit-scrollbar-track-piece {
			//滚动条凹槽的颜色，还可以设置边框属性
			background-color: #f1f1f1;
		}

		.el-table__body-wrapper::-webkit-scrollbar {
			//滚动条的宽度
			width: 10px;
			height: 10px;
		}

		.el-table__body-wrapper::-webkit-scrollbar-thumb {
			//滚动条的设置
			background-color: #c1c1c1;
			background-clip: padding-box;
			min-height: 28px;
			border-radius: 8px;
		}

		.el-table__body-wrapper::-webkit-scrollbar-thumb:hover {
			background-color: #a8a8a8;
		}
	}
</style>
